<template>
  <div class="agencies-container">
    <TableFiltersContainer padding="2px 0px 12px 0px">
      <AwTextField
        v-model="agencyState.selectedFilters.search"
        leading-icon="i-ph-magnifying-glass"
        placeholder="Rechercher une agence"
        class="min-w-fit"
        :debounce-delay="200"
      />
      <TableStatusSelect
        v-model:status="agencyState.selectedFilters.status"
        :status-options="[
          { label: 'Lead', value: 'lead' },
          { label: 'Prospect', value: 'prospect' },
          { label: 'Client', value: 'client' },
        ]"
      />
      <TableFiltersResetButton @click="resetFilters" />
    </TableFiltersContainer>
    <TableInteractive
      :key="tableKey"
      v-model:sorting="agencyState.sorting"
      :loading="agencyState.loading"
      :enable-sorting="true"
      :manual-sorting="true"
      :data="agencies"
      :columns="tableColumns"
      :empty-state-type="emptyStateType"
      :empty-state-reset-filters="resetFilters"
      height="100%"
    >
      <template #status-cell="{ row }">
        <TableCellContainer :is-loading="agencyState.loading" :skeletons="['text']">
          <div class="w-14 flex">
            <ContactStatusBadge :status="row.original.status" />
          </div>
        </TableCellContainer>
      </template>
      <template #city-cell="{ row }">
        <TableCellContainer :is-loading="agencyState.loading" :skeletons="['text']">
          <div style="display: flex; flex-direction: row; gap: 4px; align-items: center">
            <TextEllipsis>
              {{ row.original.address?.city ?? "-" }}
            </TextEllipsis>
            <div v-if="row.original.address?.postal_code">({{ row.original.address?.postal_code }})</div>
            <div v-if="row.original.is_head_office">
              <AwBadge color="blue">Siège</AwBadge>
            </div>
          </div>
        </TableCellContainer>
      </template>
      <template #street-cell="{ row }">
        <TableCellContainer :is-loading="agencyState.loading" :skeletons="['text']">
          <div style="display: flex; flex-direction: row; gap: 4px; align-items: center">
            <TextEllipsis>
              {{ row.original.address?.street1 ?? "-" }}
              {{ row.original.address?.street2 }}
            </TextEllipsis>
            <Copy v-if="row.original.address?.formatted" :text="row.original.address?.formatted ?? ''" />
          </div>
        </TableCellContainer>
      </template>
      <template #contracts-cell="{ row }">
        <TableCellContainer :is-loading="agencyState.loading" :skeletons="['amount']">
          <TextEllipsis>
            <div style="display: flex; align-items: center; flex-direction: row; gap: 4px">
              <AwIcon name="i-ph-file-text" :size="16" color="var(--gray-2)" />
              {{ row.original.contracts.length }}
            </div>
          </TextEllipsis>
        </TableCellContainer>
      </template>
      <template #siret-cell="{ row }">
        <TableCellContainer :is-loading="agencyState.loading" :skeletons="['text']">
          <TextEllipsis>
            <div v-if="row.original.siret" class="font-medium flex items-center gap-2">
              {{ row.original.siret }}
              <Copy v-if="row.original.siret" :text="row.original.siret ?? ''" />
            </div>
            <div v-else>-</div>
          </TextEllipsis>
        </TableCellContainer>
      </template>
      <template #footer>
        <TablePagination
          :total-items="agencyState.totalItems"
          :page-size="agencyState.selectedFilters.pageSize"
          :page-index="agencyState.selectedFilters.pageIndex"
          @update:page-size="agencyState.selectedFilters.pageSize = $event"
          @update:page-index="agencyState.selectedFilters.pageIndex = $event"
        />
      </template>
    </TableInteractive>
  </div>
</template>

<script setup lang="ts">
import { useToast } from "~/composables/useToast";
import { useAgencies } from "~/components/agency/composables/useAgency";
import type { TableEmptyStateType } from "~/utils/types";
import { useTableColumn } from "~/composables/useTableColumn";
import type { Agency } from "@asap/shared/schemas/agency";

const props = defineProps<{
  companyId: string;
}>();

const agencies = ref<Agency[]>([]);

const emptyStateType = ref<TableEmptyStateType>("no-data");

const { addToastError } = useToast();
const { agencyState, selectMultipleForTable, convertSortingToOrders } = useAgencies();

const { tableKey, tableColumns } = useTableColumn(
  [
    {
      label: "Statut",
      value: {
        accessorKey: "status",
        header: "Statut",
        maxSize: 60,
      },
      blocked: true,
      selected: true,
    },
    {
      label: "Ville (Code postal)",
      value: {
        accessorKey: "city",
        header: "Ville (Code postal)",
      },
      blocked: true,
      selected: true,
    },
    {
      label: "Rue",
      value: {
        accessorKey: "street",
        header: "Rue",
        minSize: 300,
      },
      blocked: true,
      selected: true,
    },
    {
      label: "Contrats",
      value: {
        accessorKey: "contracts",
        header: "Contrats",
        maxSize: 60,
      },
      blocked: true,
      selected: true,
    },
    {
      label: "SIRET",
      value: {
        accessorKey: "siret",
        header: "SIRET",
      },
      blocked: true,
      selected: true,
    },
  ],
  "agencies-table-columns"
);

const resetFilters = () => {
  agencyState.selectedFilters.search = undefined;
  agencyState.selectedFilters.status = undefined;
  agencyState.sorting = [{ id: "created_at", desc: true }];
};

const query = async function () {
  try {
    const orders = convertSortingToOrders(agencyState.sorting);
    const { data, count } = await selectMultipleForTable({
      ...agencyState.selectedFilters,
      companyId: props.companyId,
      orders,
    });
    if (data) agencies.value = data;
    if (count !== null) agencyState.totalItems = count;
    if (agencyState.selectedFilters.search) {
      emptyStateType.value = "no-data-with-filters";
    } else {
      emptyStateType.value = "no-data";
    }
  } catch (error) {
    emptyStateType.value = "error";
    addToastError(
      {
        title: "Une erreur est survenue lors de la récupération des agences",
      },
      error
    );
  }
};

const isInitialized = ref(false);

onMounted(async () => {
  agencyState.selectedFilters.pageIndex = 0;
  await query();
  isInitialized.value = true;
});

watch(() => agencyState.selectedFilters.pageIndex, query);

watch(
  () => [
    agencyState.sorting,
    agencyState.selectedFilters.search,
    agencyState.selectedFilters.pageSize,
    agencyState.selectedFilters.status,
    agencyState.selectedFilters.companyId,
    agencyState.selectedFilters.orders,
  ],
  async () => {
    if (!isInitialized.value) return;
    agencyState.selectedFilters.pageIndex = 0;
    await query();
  }
);
</script>

<style scoped lang="scss">
.agencies-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 1;
}

.toggle_wrapper {
  margin: 0 auto;
  margin-top: 1rem;
}
</style>
